import React, {
  ComponentType,
  ReactElement,
  isValidElement,
  useState
} from 'react';
import styled from 'styled-components';
import {
  Image,
  StructuredText as StructuredTextBase,
  StructuredTextGraphQlResponse,
  renderNodeRule
} from 'react-datocms';
import {
  Stack,
  Table,
  Button,
  mq,
  BodyText,
  Spacer,
  resetButtonStyle
} from '@kvdbil/components';
import Link from '~/App/shared/components/Link';
import { TableProps } from '@kvdbil/components/types/components/Table';
import { Color, Size } from '@kvdbil/components/types/Types';
import {
  InlineImageFragment,
  NewsletterSignupRecord,
  CarInspirationListRecord,
  HtmlInputRecord,
  TextGridRecord,
  TextSectionRecord,
  DynamicPageFormRecord,
  DynamicPageCustomElementRecord,
  TextTabsBlockRecord,
  DynamicPageValuationFormRecord,
  ButtonModelRecord,
  FeaturedBlocksBlockRecord,
  YoutubeClipRecord,
  ButtonRecord,
  FormRecord,
  ContactCardsBlockRecord,
  AnchorNavigationRecord,
  LeasingPartnerNavigationRecord,
  LeasingPartnerPresentationRecord,
  FaqSectionBlockRecord,
  SupportChatCardRecord,
  StructuredTextCardRecord,
  SpacerRecord
} from '~/config/generated/graphql';
import Section, {
  BackgroundColor,
  ContentBackgroundColor,
  ContentBorderColor
} from '~/App/shared/components/Layout/Section';
import CarInspirationList from '~/App/views/Blog/components/CarInspirationList';
import {
  renderRule,
  isParagraph,
  isLink,
  isHeading,
  isEmptyDocument
} from 'datocms-structured-text-utils';
import {
  CustomInlineRecord,
  CustomRecord,
  StructuredTextData
} from '~/App/shared/interfaces/datoCMS';
import TextTabsBlock from '~/App/views/DynamicPage/components/TextTabsBlock';
import ValuationForm from '~/App/views/DynamicPage/components/forms/ValuationForm';
import FeatureBlocksItems from '~/App/views/Start/components/FeatureBlocksItems';
import Auction from '~/App/shared/interfaces/Auction';
import DynamicPageFormDispatcher from '~/App/views/DynamicPage/components/DynamicPageFormDispatcher';
import DynamicPageCustomElemDispatcher from '~/App/views/DynamicPage/components/DynamicPageCustomElemDispatcher';
import YouTubePlayer from '~/App/views/ObjectPage/components/YouTubePlayer';
import { slugify } from '~/helpers/slugify';
import { HEADER_MIN_HEIGHT } from '~/App/components/Header';
import {
  getHeadingByNodeLevel,
  getHeadingByRecordTypename
} from '~/App/components/DatoCMS/StructuredText/utils';
import NewsletterTypeDispatcher from '~/App/views/Blog/components/NewsletterTypeDispatcher';
import DynamicFormDisplayController from '~/App/views/DynamicPage/components/DynamicFormDisplayController';
import ContactCards from '~/App/views/DynamicPage/components/ContactCards';
import AnchorNavigation from '~/App/views/DynamicPage/components/AnchorNavigation';
import LeasingPartnerNavigation from '~/App/views/DynamicPage/components/LeasingPartnerNavigation';
import LeasingPartnerPresentation from '~/App/views/DynamicPage/components/LeasingPartnerPresentation';
import FaqSection from '~/App/views/DynamicPage/components/FaqSection';
import SupportChatCard from '~/App/views/DynamicPage/components/SupportChatCard';
import ImageLightbox, {
  LightboxImage
} from '~/App/components/DatoCMS/StructuredText/ImageLightbox';
import AiGeneratedImageWrapper from '~/App/components/DatoCMS/AiGeneratedImageWrapper';
import { useTranslation } from '~/Locale';

const SCROLL_MARGIN_OFFSET = 20;
const SCROLL_MARGIN_TOP_MOBILE = SCROLL_MARGIN_OFFSET / 16;
const SCROLL_MARGIN_TOP = (HEADER_MIN_HEIGHT + SCROLL_MARGIN_OFFSET) / 16;

interface InlineimageWrapperPorps {
  $minMaxWidth?: string;
}

const InlineimageWrapper = styled.div<InlineimageWrapperPorps>`
  ${mq('mobileL')} {
    ${({ $minMaxWidth }) =>
      $minMaxWidth && `max-width: ${$minMaxWidth}; min-width: ${$minMaxWidth};`}
  }
`;

const ImageButton = styled.button`
  ${resetButtonStyle}
  display: block;
  width: 100%;
  cursor: zoom-in;

  &:focus-visible {
    outline: revert;
  }
`;

const InlineimageAiWrapper = styled(AiGeneratedImageWrapper)<{
  $maxWidth?: string;
}>`
  ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth};`}
`;

const StructuredTextWrapper = styled.div<WrapperProps>`
  > h1 {
    margin: revert;
    padding: revert;
    margin-bottom: 1rem;
  }

  > h2,
  > h3,
  > h4,
  > h5,
  > h6 {
    padding: revert;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  [id] {
    scroll-margin-top: ${SCROLL_MARGIN_TOP_MOBILE}rem;
  }

  ${mq('tablet')} {
    [id] {
      scroll-margin-top: ${SCROLL_MARGIN_TOP}rem;
    }
  }

  > p {
    margin: revert;
    padding: revert;
  }

  b,
  strong,
  em,
  i {
    font-style: revert;
  }

  > ul {
    list-style: disc;
  }

  > ol {
    list-style: decimal;
  }

  > ul,
  ol {
    margin: 1rem 0;
    padding: revert;

    li > p {
      margin: revert;
      padding: revert;
    }
  }

  ${({ contentTextAlign }) =>
    contentTextAlign && `text-align: ${contentTextAlign};`}
`;

const TextGridWrapper = styled.div<{ $columnGap: number; $rowGap: number }>`
  display: grid;
  grid-template-rows: 1fr;
  row-gap: ${({ $rowGap }) => $rowGap}px;

  ${mq('tablet')} {
    grid-template-columns: repeat(
      2,
      calc(50% - ${({ $columnGap }) => $columnGap}px / 2)
    );
    column-gap: ${({ $columnGap }) => $columnGap}px;
  }
`;

const TextGridStructuredTextWrapper = styled(StructuredTextWrapper)`
  & > *:first-child {
    margin-top: 0;
  }
`;

const CardWrapper = styled(StructuredTextWrapper)`
  padding: 0 1rem;
  background-color: ${({ theme }) => theme.colors.background.lightGray};
  border: 2px solid ${({ theme }) => theme.colors.background.gray};
  border-radius: 0.5rem;
`;

const StyledLink = styled(Link)`
  display: inline-block;
`;

type WrapperProps = {
  contentTextAlign?: string | null;
};

export type StructuredTextProps = {
  data?: StructuredTextData | null;
  wrapper?: ComponentType<React.PropsWithChildren<WrapperProps>>;
  wrapperProps?: WrapperProps;
  prefetchedData?: { objects?: Record<string, Auction[]> | null };
  options?: { paragraphElem: 'p' | 'span' };
};

const StructuredText = ({
  data,
  wrapper: CustomWrapper = StructuredTextWrapper,
  wrapperProps,
  prefetchedData,
  options
}: StructuredTextProps) => {
  const { t } = useTranslation();
  const [openedImage, setOpenedImage] = useState<LightboxImage | null>(null);

  const handleCloseLightbox = () => {
    setOpenedImage(null);
  };

  const { paragraphElem = 'p' } = options ?? {};
  return (
    <CustomWrapper {...wrapperProps}>
      <StructuredTextBase
        data={data as StructuredTextGraphQlResponse}
        customNodeRules={[
          renderNodeRule(isLink, ({ node, children, key }) => {
            const skipAutoNofollowAddition = node?.meta?.some(
              ({ id, value }) => id === 'custom' && value === 'dofollow'
            );
            const metaProps = node?.meta?.reduce(
              (acc, { id, value }) => {
                if (id !== 'custom') {
                  acc[id] = value;
                }
                return acc;
              },
              {} as Record<string, string>
            );
            return (
              <Link
                key={key}
                to={node.url}
                {...metaProps}
                skipAutoNofollowAddition={skipAutoNofollowAddition}
                withUnderline
              >
                {children}
              </Link>
            );
          }),
          renderNodeRule(
            isHeading,
            ({ adapter: { renderNode }, node, children, key }) => {
              const headingText = (children?.[0] as ReactElement)?.props
                ?.children?.[0];
              return renderNode(
                getHeadingByNodeLevel(node.level),
                {
                  key,
                  as: `h${node.level}`,
                  id:
                    typeof headingText === 'string'
                      ? slugify(headingText)
                      : undefined
                },
                children
              );
            }
          ),
          renderRule(
            isParagraph,
            ({ adapter: { renderNode }, children, key }) => {
              const containsForm = children?.some(
                child =>
                  isValidElement<{ record?: CustomInlineRecord }>(child) &&
                  child.props.record?.__typename === 'FormRecord'
              );
              if (containsForm) {
                return renderNode(BodyText, { key, as: 'div' }, children);
              }
              return renderNode(BodyText, { key, as: paragraphElem }, children);
            }
          )
        ]}
        renderBlock={({ record }) => {
          const recordTypename =
            record.__typename as CustomRecord['__typename'];
          switch (recordTypename) {
            case 'BodyTextRecord':
              return (
                <BodyText as={(record?.tag ?? 'p') as never}>
                  {record.text as typeof record}
                </BodyText>
              );

            case 'ButtonRecord':
              const { url, text, color, size, openLinkInNewWindow } =
                record as ButtonRecord;
              return (
                <StyledLink
                  target={openLinkInNewWindow ? '_blank' : undefined}
                  to={url ?? '/'}
                >
                  <Button as="span" size={size as Size} color={color as Color}>
                    {text}
                  </Button>
                </StyledLink>
              );

            case 'InlineimageRecord':
              const {
                lefttext,
                righttext,
                image,
                imageMaxWidth,
                textPlacement,
                textContent
              } = record as InlineImageFragment;
              if (!image) {
                return <></>;
              }
              const {
                responsiveImage,
                lightboxImage,
                url: svgImageUrl,
                alt,
                tags
              } = image;
              const isStructuredText = !isEmptyDocument(textContent);
              const hasText = Boolean(
                lefttext || righttext || isStructuredText
              );
              const minMaxWidth =
                imageMaxWidth || (hasText ? '50%' : undefined);
              const imageMaxWidthStyle =
                !hasText && imageMaxWidth ? imageMaxWidth : undefined;

              const StructuredTextContent = () => (
                <StructuredText
                  data={textContent}
                  wrapper={TextGridStructuredTextWrapper}
                />
              );
              return (
                <Stack direction={{ mobileS: 'column', mobileL: 'row' }}>
                  {!isStructuredText && lefttext && (
                    <BodyText>{lefttext}</BodyText>
                  )}
                  {isStructuredText && textPlacement === 'left' && (
                    <StructuredTextContent />
                  )}
                  {(responsiveImage || svgImageUrl) && (
                    <InlineimageWrapper $minMaxWidth={minMaxWidth}>
                      <InlineimageAiWrapper
                        tags={tags}
                        $maxWidth={imageMaxWidthStyle}
                      >
                        {responsiveImage &&
                          (lightboxImage ? (
                            <ImageButton
                              type="button"
                              aria-label={t('Open image in fullscreen')}
                              onClick={() =>
                                setOpenedImage({
                                  responsiveImage: lightboxImage,
                                  tags
                                })
                              }
                            >
                              <Image
                                data={responsiveImage}
                                style={{ maxWidth: imageMaxWidthStyle }}
                              />
                            </ImageButton>
                          ) : (
                            <Image
                              data={responsiveImage}
                              style={{ maxWidth: imageMaxWidthStyle }}
                            />
                          ))}
                        {!responsiveImage && svgImageUrl && (
                          <img
                            src={svgImageUrl}
                            alt={alt ?? 'svg-image'}
                            width="100%"
                          />
                        )}
                      </InlineimageAiWrapper>
                    </InlineimageWrapper>
                  )}

                  {!isStructuredText && righttext && (
                    <BodyText>{righttext}</BodyText>
                  )}
                  {isStructuredText && textPlacement === 'right' && (
                    <StructuredTextContent />
                  )}
                </Stack>
              );

            case 'TableRecord':
              const structure = record.tablestructure as TableProps;
              if (structure?.columns?.[0] === 'Cookie') {
                return null;
              }
              return (
                <Table columns={structure.columns} data={structure.data} />
              );

            case 'HeadlineRecord':
            case 'TitleRecord':
            case 'SubtitleRecord':
              const headingId =
                typeof record.text === 'string'
                  ? slugify(record.text)
                  : undefined;
              const HeadingElement = getHeadingByRecordTypename(
                recordTypename as
                  | 'HeadlineRecord'
                  | 'TitleRecord'
                  | 'SubtitleRecord'
              );
              return (
                <HeadingElement
                  as={(record?.tag ?? 'h2') as never}
                  id={headingId}
                >
                  {record.text as typeof record}
                </HeadingElement>
              );

            case 'NewsletterSignupRecord':
              const { title, description, newsletterType } =
                record as NewsletterSignupRecord;
              return (
                <Section spacing={2} backgroundColor="sand" maxWidth={35.5}>
                  <NewsletterTypeDispatcher
                    title={title}
                    description={description}
                    newsletterType={newsletterType}
                  />
                </Section>
              );

            case 'CarInspirationListRecord':
              const { id, queryString, heading, linkText } =
                record as CarInspirationListRecord;

              if (!queryString) {
                return null;
              }

              return (
                <CarInspirationList
                  queryString={queryString}
                  heading={heading ?? ''}
                  linkText={linkText ?? ''}
                  prefetchedObjects={prefetchedData?.objects?.[id]}
                />
              );

            case 'HtmlInputRecord':
              const { htmlField } = record as HtmlInputRecord;

              if (!htmlField) {
                return null;
              }

              return (
                <div
                  dangerouslySetInnerHTML={{
                    __html: htmlField
                  }}
                />
              );

            case 'TextSectionRecord':
              const {
                body: sectionBody,
                contentMaxWidth,
                contentBackgroundColor,
                contentPadding,
                contentTextAlign,
                paddingTop,
                paddingBottom,
                backgroundColor,
                contentBorderColor,
                innerContentMaxWidth
              } = record as TextSectionRecord;
              return (
                <Section
                  backgroundColor={backgroundColor as BackgroundColor}
                  maxWidth={(contentMaxWidth ?? 768) / 16}
                  spacingTop={(paddingTop ?? 24) / 16}
                  spacingBottom={(paddingBottom ?? 24) / 16}
                  contentBackgroundColor={
                    contentBackgroundColor as ContentBackgroundColor
                  }
                  contentPadding={contentPadding ? contentPadding : undefined}
                  contentBorderColor={contentBorderColor as ContentBorderColor}
                  innerContentMaxWidth={innerContentMaxWidth}
                >
                  <StructuredText
                    data={sectionBody}
                    wrapperProps={{ contentTextAlign }}
                    prefetchedData={prefetchedData}
                  />
                </Section>
              );

            case 'TextGridRecord':
              const { columnGap, rowGap, columns } = record as TextGridRecord;
              return (
                <TextGridWrapper
                  $columnGap={columnGap || 80}
                  $rowGap={rowGap || 16}
                >
                  {columns?.map((column, key) => {
                    if (!isEmptyDocument(column?.body)) {
                      return (
                        <StructuredText
                          data={column?.body}
                          key={`column-${key}`}
                          wrapper={TextGridStructuredTextWrapper}
                        />
                      );
                    }
                  })}
                </TextGridWrapper>
              );

            case 'DynamicPageFormRecord':
              return (
                <DynamicPageFormDispatcher
                  record={record as DynamicPageFormRecord}
                />
              );
            case 'DynamicPageCustomElementRecord':
              return (
                <DynamicPageCustomElemDispatcher
                  record={record as DynamicPageCustomElementRecord}
                />
              );
            case 'TextTabsBlockRecord':
              return <TextTabsBlock record={record as TextTabsBlockRecord} />;
            case 'DynamicPageValuationFormRecord':
              return (
                <ValuationForm
                  record={record as DynamicPageValuationFormRecord}
                />
              );
            case 'FeaturedBlocksBlockRecord':
              const { blockButtons } = record as FeaturedBlocksBlockRecord;
              return <FeatureBlocksItems blockButtons={blockButtons} />;
            case 'YoutubeClipRecord':
              const { videoId, linkParameters, width, aspectRatio } =
                record as YoutubeClipRecord;
              return videoId ? (
                <YouTubePlayer
                  videoId={videoId}
                  linkParameters={linkParameters ?? ''}
                  width={width}
                  aspectRatio={aspectRatio ?? undefined}
                />
              ) : null;
            case 'ContactCardsBlockRecord':
              return (
                <ContactCards record={record as ContactCardsBlockRecord} />
              );
            case 'AnchorNavigationRecord':
              const { items: anchorItems } = record as AnchorNavigationRecord;
              return <AnchorNavigation items={anchorItems} />;
            case 'LeasingPartnerNavigationRecord':
              const {
                title: leasingNavigationTitle,
                helpText: leasingNavigationHelpText,
                partnerLogos
              } = record as LeasingPartnerNavigationRecord;
              return (
                <LeasingPartnerNavigation
                  title={leasingNavigationTitle}
                  helpText={leasingNavigationHelpText}
                  partnerLogos={partnerLogos}
                />
              );
            case 'LeasingPartnerPresentationRecord':
              const {
                anchorId,
                partnerName,
                leasingProvider,
                logo: partnerLogo,
                description: partnerDescription,
                uspHeading,
                uspList,
                linkButton,
                promotionHeading,
                promotionVehicleCards,
                promotionButton,
                interestModalTitle,
                interestModalIntro,
                priceFootnote,
                interestConsentLabel,
                interestSubmitButtonText,
                interestConfirmationTitle,
                interestConfirmationText,
                interestConfirmationSubjectHeading,
                interestConfirmationDetailsHeading
              } = record as LeasingPartnerPresentationRecord;
              return (
                <LeasingPartnerPresentation
                  anchorId={anchorId}
                  partnerName={partnerName}
                  leasingProvider={leasingProvider}
                  logo={partnerLogo}
                  description={partnerDescription}
                  uspHeading={uspHeading}
                  uspList={uspList}
                  linkButton={linkButton}
                  promotionHeading={promotionHeading}
                  promotionVehicleCards={promotionVehicleCards}
                  promotionButton={promotionButton}
                  interestModalTitle={interestModalTitle}
                  interestModalIntro={interestModalIntro}
                  priceFootnote={priceFootnote}
                  interestConsentLabel={interestConsentLabel}
                  interestSubmitButtonText={interestSubmitButtonText}
                  interestConfirmationTitle={interestConfirmationTitle}
                  interestConfirmationText={interestConfirmationText}
                  interestConfirmationSubjectHeading={
                    interestConfirmationSubjectHeading
                  }
                  interestConfirmationDetailsHeading={
                    interestConfirmationDetailsHeading
                  }
                />
              );
            case 'FaqSectionBlockRecord':
              const {
                title: faqTitle,
                ingress: faqIngress,
                questions: faqQuestions,
                footerText,
                footerLink
              } = record as FaqSectionBlockRecord;
              return (
                <FaqSection
                  title={faqTitle}
                  ingress={faqIngress}
                  questions={faqQuestions}
                  footerText={footerText}
                  footerLink={footerLink}
                />
              );
            case 'SupportChatCardRecord':
              return (
                <SupportChatCard record={record as SupportChatCardRecord} />
              );
            case 'StructuredTextCardRecord':
              const { body: structuredTextBody } =
                record as StructuredTextCardRecord;
              return (
                <StructuredText
                  data={structuredTextBody}
                  wrapper={CardWrapper}
                />
              );
            case 'SpacerRecord':
              const { space } = record as SpacerRecord;
              return <Spacer height={space} />;

            default:
              return null;
          }
        }}
        renderInlineRecord={({ record }) => {
          switch (record.__typename as CustomInlineRecord['__typename']) {
            case 'ButtonModelRecord':
              const { url, text, color, size, openLinkInNewWindow } =
                record as ButtonModelRecord;
              return (
                <StyledLink
                  target={openLinkInNewWindow ? '_blank' : undefined}
                  to={url ?? '/'}
                >
                  <Button as="span" size={size as Size} color={color as Color}>
                    {text}
                  </Button>
                </StyledLink>
              );
            case 'FormRecord':
              return (
                <DynamicFormDisplayController record={record as FormRecord} />
              );
            default:
              return null;
          }
        }}
      />
      <ImageLightbox
        isOpen={openedImage !== null}
        image={openedImage}
        onClose={handleCloseLightbox}
      />
    </CustomWrapper>
  );
};

export default StructuredText;
